import { localStorageAdapter } from "@policystack/core/consent/storage/local-storage";
import {
  Compliance,
  ContractPrerequisite,
  Contractual,
  defineConfig,
  LegalBases,
  Providers,
  Retention,
  Voluntary,
} from "@policystack/sdk";

import * as scanned from "./policystack.gen";

export default defineConfig(
  {
    company: {
      name: "Daily Wars",
      legalName: "Elias Brange",
      address: "Sweden",
      contact: {
        email: "privacy@playdailywars.com",
      },
      dpo: { required: false },
    },
    effectiveDate: "2026-05-19",
    children: { underAge: 16 },
    jurisdictions: [
      ...Compliance.GDPR.jurisdictions,
      ...Compliance.UK_GDPR.jurisdictions,
      "us",
      ...Compliance.CCPA.jurisdictions,
      "us-co",
      "us-ct",
      "us-va",
      "row",
    ],
    data: {
      collected: {
        "Account Information": ["User ID", "Email address", "Profile image"],
        "Player Profile": ["Player ID", "Display name", "Lifetime statistics"],
        Gameplay: ["Gameplay data"],
        "Billing Information": ["Polar customer ID", "Subscription status"],
        "Anonymous Identifiers": ["Anonymous player ID"],
        "Usage Analytics": [
          "Connection data (IP address and browser type — used momentarily, never stored)",
          "Page views and gameplay events (linked only to an anonymous daily visitor number)",
        ],
        Preferences: ["Cookie and analytics consent choices"],
      },
      context: {
        "Account Information": {
          purpose:
            "Create and manage accounts, authenticate users, and link gameplay to signed-in profiles",
          lawfulBasis: LegalBases.Contract,
          retention: Retention.UntilAccountDeletion,
          provision: Contractual(
            "You cannot create an account or sign in without providing this information.",
          ),
        },
        "Player Profile": {
          purpose:
            "Store player display names, streaks, and lifetime statistics shown on profiles and leaderboards",
          lawfulBasis: LegalBases.Contract,
          retention: Retention.UntilAccountDeletion,
          provision: ContractPrerequisite(
            "Guest play uses an anonymous ID instead; signed-in profiles require an account.",
          ),
        },
        Gameplay: {
          purpose:
            "Save in-progress and completed daily map runs, process turn submissions, and rank scores on leaderboards",
          lawfulBasis: LegalBases.LegitimateInterests,
          retention:
            "Until account deletion; anonymous in-progress runs expire after about 24 hours",
          provision: Voluntary(
            "You can browse maps without playing; starting a run stores gameplay data needed to continue and score the match.",
          ),
        },
        "Billing Information": {
          purpose:
            "Unlock Premium features based on subscription status synced from Polar, which sells subscriptions as Merchant of Record",
          lawfulBasis: LegalBases.Contract,
          retention: Retention.UntilAccountDeletion,
          provision: Contractual(
            "Required to purchase or manage Premium; Polar handles payment, tax, and the sale. We do not receive card numbers, billing addresses, or full transaction history.",
          ),
        },
        "Anonymous Identifiers": {
          purpose:
            "Assign and remember a guest player ID for the browser session so anonymous runs can continue while the session is open",
          lawfulBasis: LegalBases.LegitimateInterests,
          retention: Retention.UntilSessionExpiry,
          provision: Voluntary(
            "Guest play requires this identifier for the current browser session; signing in replaces it with your account ID and clears the anonymous ID.",
          ),
        },
        "Usage Analytics": {
          purpose:
            "Understand how many people play and which features get used, without cookies or persistent identifiers, when you have not opted in to analytics. Visitor counts are derived from your IP address and browser using a privacy-preserving hash and rotate daily",
          lawfulBasis: LegalBases.LegitimateInterests,
          retention:
            "Your IP address and browser details are discarded the moment the hash is computed and are never stored. The hash itself changes every day and cannot be traced back to you",
          provision: Voluntary(
            "This measurement runs unless you opt in to analytics cookies, which replaces it with cookie-based analytics. You may object to it at any time by contacting us.",
          ),
        },
        Preferences: {
          purpose: "Remember your cookie and analytics consent choices in the browser",
          lawfulBasis: LegalBases.LegitimateInterests,
          retention: "Until you clear site data or change the preference",
          provision: Voluntary(
            "Consent storage is optional; without it you may see the consent prompt again on later visits.",
          ),
        },
      },
    },
    thirdParties: [
      Providers.Clerk,
      Providers.AWS,
      Providers.PostHog,
      {
        name: "Polar",
        purpose:
          "Merchant of Record; sells and processes Premium subscriptions, payment, and tax on our behalf",
        policyUrl: "https://polar.sh/legal/privacy-policy",
      },
    ],
    automatedDecisionMaking: [],
    trackingTechnologies: [
      'Essential — session storage key "aw_player_id" (guest player ID for the current browser session)',
      'Essential — session cookie "aw_player_id" (guest player ID for the current browser session)',
      'Essential — local storage key "dailywars-consent" (your cookie preference choices)',
      'Essential — local storage key "__ph_opt_in_out_*" (records your analytics opt-in/opt-out choice)',
      "Analytics — PostHog cookies and local storage for session analytics when you opt in",
      "Analytics — when you have not opted in, visits are counted without cookies using an anonymous, daily-changing hash; nothing is stored on your device",
    ],
    cookies: {
      used: { essential: true, analytics: true },
      context: {
        essential: { lawfulBasis: LegalBases.Contract },
        analytics: { lawfulBasis: LegalBases.Consent },
      },
    },
    consent: {
      adapter: localStorageAdapter({ key: "dailywars-consent" }),
    },
  },
  scanned,
);
